import api from './axiosInstance';

export interface State {
  name: string;
  iso2: string;
}

export interface City {
  name: string;
}

export interface CitySearchResult {
  name: string;
  state: string;
}

export interface ValidationResult {
  isValid: boolean;
  [key: string]: any;
}

export interface CompleteLocationData {
  country: string;
  countryCode: string;
  states: Array<State & { cities: City[] }>;
}

class LocationDropdownService {
  /**
   * Get all Indian states
   */
  async getStates(): Promise<State[]> {
    try {
      const response = await api.get('/locations/dropdown/states');
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching states:', error);
      throw new Error('Failed to fetch states');
    }
  }

  /**
   * Get cities for a specific state
   */
  async getCitiesByState(stateIso2: string): Promise<City[]> {
    try {
      const response = await api.get(`/locations/dropdown/cities/${stateIso2}`);
      return response.data.data || [];
    } catch (error) {
      console.error(`Error fetching cities for state ${stateIso2}:`, error);
      throw new Error(`Failed to fetch cities for ${stateIso2}`);
    }
  }

  /**
   * Search cities by name with optional state filter
   */
  async searchCities(query: string, stateIso2?: string): Promise<CitySearchResult[]> {
    try {
      const params = new URLSearchParams({ query });
      if (stateIso2) {
        params.append('state', stateIso2);
      }
      
      const response = await api.get(`/locations/dropdown/cities/search?${params}`);
      return response.data.data || [];
    } catch (error) {
      console.error('Error searching cities:', error);
      throw new Error('Failed to search cities');
    }
  }

  /**
   * Validate if a state exists in India
   */
  async validateState(stateName: string): Promise<ValidationResult> {
    try {
      const response = await api.post('/locations/dropdown/validate/state', {
        stateName
      });
      return response.data.data;
    } catch (error) {
      console.error('Error validating state:', error);
      throw new Error('Failed to validate state');
    }
  }

  /**
   * Validate if a city exists in a specific state
   */
  async validateCity(cityName: string, stateIso2: string): Promise<ValidationResult> {
    try {
      const response = await api.post('/locations/dropdown/validate/city', {
        cityName,
        stateIso2
      });
      return response.data.data;
    } catch (error) {
      console.error('Error validating city:', error);
      throw new Error('Failed to validate city');
    }
  }

  /**
   * Get complete location data (states with major cities)
   */
  async getCompleteLocationData(): Promise<CompleteLocationData> {
    try {
      const response = await api.get('/locations/dropdown/complete');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching complete location data:', error);
      throw new Error('Failed to fetch complete location data');
    }
  }
}

export const locationDropdownService = new LocationDropdownService();
